import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { properties as mockProperties } from "@/mock";
import { useSites, useProjects, kindOf, hueFor, deviceHint, type PropertyKind } from "@/data/hooks";
import type { Project, Site } from "@/data/services";

export type Range = "24h" | "7d" | "30d" | "90d";

/** A property as the UI sees it — real site mapped, or mock fallback. */
export interface WsProperty {
  id: string; // real site_id, or mock id
  name: string;
  domain: string;
  kind: "web" | "ios" | "android" | "server"; // site type
  propertyKind: PropertyKind; // built | connected | app  (drives per-kind nav)
  hue: string;
  site?: Site;
}

interface WorkspaceState {
  properties: WsProperty[];
  current: WsProperty;
  setCurrent: (id: string) => void;
  /** real analytics site id for the current property (undefined when mock fallback) */
  siteId: string | undefined;
  /** builder project bridge */
  projects: Project[];
  currentProjectId: string | undefined;
  setCurrentProject: (id: string) => void;
  range: Range;
  setRange: (r: Range) => void;
  /** true when backed by real backend data (not mock fallback) */
  live: boolean;
  loading: boolean;
}

const Ctx = createContext<WorkspaceState | null>(null);

function mapSite(s: Site, i: number): WsProperty {
  return {
    id: s.site_id,
    name: s.name,
    domain: s.urls[0] ?? deviceHint(s.type),
    kind: s.type,
    propertyKind: kindOf(s),
    hue: hueFor(i),
    site: s,
  };
}

const mockAsProps: WsProperty[] = mockProperties.map((p, i) => ({
  id: p.id,
  name: p.name,
  domain: p.domain,
  kind: p.kind,
  propertyKind: p.kind === "ios" || p.kind === "android" ? "app" : "built",
  hue: p.hue ?? hueFor(i),
}));

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const sitesQ = useSites();
  const projectsQ = useProjects();

  // Revoked (soft-deleted) properties are excluded from the switcher and from
  // selection here. The list endpoint still returns them (and WebPropertiesSection
  // intentionally shows them with a "Revoked" pill), so we filter at this consumer
  // only. If the currently-selected property gets revoked, `current` falls back to
  // the first active property below.
  const realProps = useMemo(
    () => (sitesQ.data?.sites ?? []).filter((s) => !s.revoked_at).map(mapSite),
    [sitesQ.data],
  );
  const live = realProps.length > 0;
  const properties = live ? realProps : mockAsProps;

  // Persist the operator's property choice. Without this the switcher reset to
  // the first "built" property on every page load — which is the auto-created,
  // usually EMPTY "Hosted pages" property — so a user who had just connected
  // their own site reloaded the app and saw "No replays yet" / empty panels,
  // concluding tracking was broken.
  const ACTIVE_PROPERTY_KEY = "dl.activeProperty";
  const [currentId, setCurrentId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_PROPERTY_KEY);
    } catch {
      return null;
    }
  });
  const [projectId, setProjectId] = useState<string | null>(null);
  const [range, setRange] = useState<Range>("7d");

  useEffect(() => {
    try {
      if (currentId) localStorage.setItem(ACTIVE_PROPERTY_KEY, currentId);
    } catch {
      /* storage unavailable (private mode) — selection just won't persist */
    }
  }, [currentId]);

  // Default selection when nothing is stored (or the stored property is gone):
  // prefer a property the operator actually CONFIGURED (has a url), which skips
  // the implicit "Hosted pages" property; then the first built site; then any.
  const current =
    properties.find((p) => p.id === currentId) ??
    properties.find((p) => (p.site?.urls?.length ?? 0) > 0) ??
    properties.find((p) => p.propertyKind === "built") ??
    properties[0] ??
    mockAsProps[0]!;
  const projects = projectsQ.data?.items ?? [];
  const currentProjectId = projectId ?? projects[0]?.id;

  const value: WorkspaceState = {
    properties,
    current,
    setCurrent: setCurrentId,
    siteId: live && current.site ? current.id : undefined,
    projects,
    currentProjectId,
    setCurrentProject: setProjectId,
    range,
    setRange,
    live,
    loading: sitesQ.isLoading,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return ctx;
}
